'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import { createContext, useContext, useState } from 'react';

import SessionLoadingGuard from './shared/SessionLoadingGuard';

/**
 * Feature flag context — values are evaluated server-side (Vercel Flags SDK)
 * and passed to client components via this context.
 */
interface FeatureFlags {
  frontendB2b: boolean;
  /** `sales-tax-reimbursement` Vercel flag (see app/lib/salesTaxReimbursement.ts). */
  salesTaxReimbursement: boolean;
}

const FeatureFlagContext = createContext<FeatureFlags>({
  frontendB2b: false,
  salesTaxReimbursement: false,
});

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}

export function Providers({
  children,
  session,
  featureFlags,
}: {
  children: React.ReactNode;
  session?: any;
  featureFlags?: FeatureFlags;
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const flags = featureFlags ?? {
    frontendB2b: false,
    salesTaxReimbursement: false,
  };

  return (
    <SessionProvider
      session={session}
      refetchOnWindowFocus={false}
      refetchWhenOffline={false}
      refetchInterval={0}
    >
      <SessionLoadingGuard session={session}>
        <QueryClientProvider client={queryClient}>
          <FeatureFlagContext.Provider value={flags}>
            {children}
          </FeatureFlagContext.Provider>
        </QueryClientProvider>
      </SessionLoadingGuard>
    </SessionProvider>
  );
}
